// src/components/Navbar.js
import React, { useState, useEffect, useCallback } from "react";
import { Link, NavLink } from "react-router-dom";
import { FaBars, FaTimes } from "react-icons/fa";
import logo from "../assets/logo.png";   // ← your logo file
import ThemeToggle from "./ThemeToggle";
import "./Navbar.css";

const NAV_LINKS = [
  { to: "/",               label: "Home"           },
  { to: "/about",          label: "About"          },
  { to: "/projects",       label: "Projects"       },
  { to: "/certifications", label: "Certifications" },
  { to: "/blog",           label: "Blog"           },
  { to: "/contact",        label: "Contact"        },
];

const Navbar = () => {
  const [open,     setOpen]     = useState(false);
  const [scrolled, setScrolled] = useState(false);

  /* ── Scroll detection ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── Lock body scroll when drawer is open ── */
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  /* ── Close on Escape key ── */
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") setOpen(false);
  }, []);

  useEffect(() => {
    if (open) document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  const close = () => setOpen(false);

  return (
    <>
      <header className={`navbar${scrolled ? " navbar--scrolled" : ""}`}>
        <div className="navbar__inner">

          {/* ── Logo ── */}
          <Link to="/" className="navbar__logo" onClick={close}>
            <span className="navbar__logo-mark">
              <img src={logo} alt="Raphael Tildai" className="navbar__logo-img" />
            </span>
            <span className="navbar__logo-text">Raphael Tildai</span>
          </Link>

          {/* ── Desktop links ── */}
          <nav className="navbar__links">
            {NAV_LINKS.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                className={({ isActive }) =>
                  `navbar__link${isActive ? " navbar__link--active" : ""}`
                }
              >
                {label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn btn-primary navbar__cta">
              Hire Me
            </Link>
            <ThemeToggle />
          </nav>

          {/* ── Hamburger ── */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <ThemeToggle className="navbar__theme-toggle--mobile" />
            <button
              className="navbar__hamburger"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              {open ? <FaTimes size={18} /> : <FaBars size={18} />}
            </button>
          </div>

        </div>
      </header>

      {/* ── Backdrop (click to close) ── */}
      <div
        className={`navbar__backdrop${open ? " navbar__backdrop--open" : ""}`}
        onClick={close}
        aria-hidden="true"
      />

      {/* ── Side Drawer ── */}
      <div
        className={`navbar__drawer${open ? " navbar__drawer--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Drawer header */}
        <div className="navbar__drawer-header">
          <Link to="/" className="navbar__drawer-logo" onClick={close}>
            <span className="navbar__drawer-logo-mark">
              <img src={logo} alt="Raphael Tildai" className="navbar__logo-img" />
            </span>
            <span className="navbar__drawer-logo-text">Raphael Tildai</span>
          </Link>
          <button
            className="navbar__drawer-close"
            onClick={close}
            aria-label="Close menu"
          >
            <FaTimes size={16} />
          </button>
        </div>

        {/* Drawer links */}
        <nav className="navbar__drawer-links">
          {NAV_LINKS.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                `navbar__drawer-link${isActive ? " navbar__drawer-link--active" : ""}`
              }
              onClick={close}
            >
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Drawer footer CTA */}
        <div className="navbar__drawer-footer">
          <Link to="/contact" className="btn btn-primary" onClick={close}>
            Hire Me
          </Link>
        </div>
      </div>
    </>
  );
};

export default Navbar;